import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useRef, useState } from 'react';
import { PortalListPresentation } from '../presentations';
import { useDeletePortal } from './useDeletePortal';
import { useGetPortalItems } from './useGetPortalItems';
import { usePatchClientPortalItemsReorder } from './usePatchClientPortalItemsReorder';

type Portal = {
  id: number;
  title: string;
  iconName: string;
  linkUrl?: string;
  fileUrl?: string;
  itemType: 'file' | 'url';
  sortOrder: number;
};

type PresentationProps = React.ComponentProps<typeof PortalListPresentation>;

type UsePortalList = () => {
  state: {
    data: ReturnType<typeof useGetPortalItems>['data'];
    popupEl: HTMLElement | null;
    selectedPortal: Portal | null;
    isOpenEditModal: boolean;
    isOpenCreateModal: boolean;
  };
  actions: {
    handleReOrder: PresentationProps['onReOrder'];
    handleOpenPortalMenu: (event: React.MouseEvent<HTMLElement>, portal: Portal) => void;
    handleClosePortalMenu: () => void;
    handleEditPortal: () => void;
    handleDeletePortal: () => void;
    handleCloseEditModal: () => void;
    handleOpenCreateModal: () => void;
    handleCloseCreateModal: () => void;
    handleRefreshPortalList: () => void;
  };
};

export const usePortalList: UsePortalList = () => {
  const threeLetterCode = getThreeLetterCode();

  // APIからデータを取得
  const { data, mutate } = useGetPortalItems(threeLetterCode, {
    swr: {
      suspense: true,
      revalidateOnFocus: false,
    },
  });

  const [popupEl, setPopupEl] = useState<HTMLElement | null>(null);
  const [selectedPortal, setSelectedPortal] = useState<Portal | null>(null);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);

  const { showToast } = useToast();
  const { showAlertDialog } = useAlertDialog();
  const selectedPortalRef = useRef<Portal | null>(null);

  const { trigger: deletePortal } = useDeletePortal(threeLetterCode, selectedPortal?.id ?? 0, {
    swr: {
      onSuccess: () => {
        // キャッシュを更新
        mutate();
        showToast({
          message: 'ポータルを削除しました。',
          status: 'success',
        });
      },
      onError: () => {
        showToast({
          message: '削除に失敗しました。',
          status: 'danger',
        });
      },
    },
  });

  const { trigger: patchPortalItemsReorder } = usePatchClientPortalItemsReorder(threeLetterCode, {
    swr: {
      onSuccess: () => {
        showToast({
          message: 'ポータルの並び順を更新しました。',
          status: 'success',
        });
      },
      onError: () => {
        showToast({
          message: '並び順の更新に失敗しました。',
          status: 'danger',
        });
      },
    },
  });

  const handleReOrder: PresentationProps['onReOrder'] = async reorderedItems => {
    const sortedIds = reorderedItems.map(item => item.id);

    // 楽観的更新：並び替え後のデータを生成
    const updatedData = {
      ...data,
      client_portal_items: reorderedItems.map((item, index) => {
        const original = data.client_portal_items.find(original => original.id === item.id);
        return {
          ...original,
          sort_order: index + 1,
        };
      }),
    };

    // SWRキャッシュを即時更新（再検証なし）
    mutate(updatedData, { revalidate: false });

    try {
      await patchPortalItemsReorder({
        sorted_client_portal_item_ids: sortedIds,
      });
    } catch {
      // エラーの場合は元のデータに戻す
      mutate();
    }
  };

  const handleOpenPortalMenu = (event: React.MouseEvent<HTMLElement>, portal: Portal) => {
    event.stopPropagation();
    setSelectedPortal(portal);
    setPopupEl(event.currentTarget);
  };

  const handleClosePortalMenu = () => {
    setPopupEl(null);
  };

  const handleEditPortal = () => {
    if (selectedPortal) {
      setIsOpenEditModal(true);
    }
    handleClosePortalMenu();
  };

  const handleDeletePortal = () => {
    if (!selectedPortal) return;

    selectedPortalRef.current = selectedPortal;
    handleClosePortalMenu();

    showAlertDialog({
      title: `「${selectedPortal.title}」を削除しますか？`,
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await deletePortal();
      },
    });
  };

  const handleCloseEditModal = () => {
    setIsOpenEditModal(false);
    setSelectedPortal(null);
  };

  const handleOpenCreateModal = () => {
    setIsOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setIsOpenCreateModal(false);
  };

  const handleRefreshPortalList = () => {
    mutate();
  };

  return {
    state: { data, popupEl, selectedPortal, isOpenEditModal, isOpenCreateModal },
    actions: {
      handleReOrder,
      handleOpenPortalMenu,
      handleClosePortalMenu,
      handleEditPortal,
      handleDeletePortal,
      handleCloseEditModal,
      handleOpenCreateModal,
      handleCloseCreateModal,
      handleRefreshPortalList,
    },
  };
};
